import {
  AddEducationRequest,
  AddProjectRequest,
  DeleteEducationRequest,
  DeleteProjectRequest,
  GetEducationRequest,
  GetEducationResponse,
  GetEducationsResponse,
  GetUserProjectRequest,
  GetUserProjectResponse,
  GetUserProjectsResponse,
  UpdateEducationRequest,
  UpdateProjectRequest,
} from '@internal/types/api';
import { Education, UserProject } from '@internal/types/experience';

import { httpClient, mutationEndpoint, queryEndpoint } from '../system';

const getProject = queryEndpoint({
  entity: 'project',
  queryKey: ({ id }: GetUserProjectRequest & { apiKey: string }) => [
    'project',
    id,
  ],
  queryFn: ({ id, apiKey }, nextContext?) =>
    httpClient.auto<GetUserProjectResponse>({
      url: `/api/user-projects/${id}`,
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: null,
  nullable: true,
});

const getProjects = queryEndpoint({
  entity: 'projects',
  queryKey: () => ['projects'],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient
      .auto<GetUserProjectsResponse>({
        url: '/api/user-projects',
        method: 'GET',
        headers: {
          'auth-key': apiKey,
        },
        nextContext,
      })
      .then(data =>
        data.sort(
          (a, b) =>
            new Date(b.startDate || '').valueOf() -
            new Date(a.startDate || '').valueOf()
        )
      ),
  defaultValue: [],
});

const addProject = mutationEndpoint({
  actionName: 'addProject',
  mutationKey: ['add-project'],
  mutationFn: ({
    apiKey,
    ...payload
  }: AddProjectRequest & { apiKey: string }) =>
    httpClient.auto<GetUserProjectResponse>({
      url: '/api/user-projects',
      method: 'POST',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, response) => {
    queryClient.setQueryData<UserProject[]>(['projects'], prev => [
      ...(prev || []),
      response,
    ]);
    queryClient.setQueryData<UserProject>(
      ['project', response.id],
      () => response
    );
  },
});

const updateProject = mutationEndpoint({
  actionName: 'updateProject',
  mutationKey: ['update-project'],
  mutationFn: ({
    apiKey,
    id,
    ...payload
  }: UpdateProjectRequest & { apiKey: string }) =>
    httpClient.auto<GetUserProjectResponse>({
      url: `/api/user-projects/${id}`,
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, response) => {
    queryClient.setQueryData<UserProject[]>(['projects'], prev =>
      prev
        ? [
            ...prev.map(project =>
              project.id === response.id ? response : project
            ),
          ]
        : [response]
    );
    queryClient.setQueryData<UserProject>(
      ['project', response.id],
      () => response
    );
  },
});

const deleteProject = mutationEndpoint({
  actionName: 'deleteProject',
  mutationKey: ['delete-project'],
  mutationFn: ({ apiKey, id }: DeleteProjectRequest & { apiKey: string }) =>
    httpClient.auto<void>({
      url: `/api/user-projects/${id}`,
      method: 'DELETE',
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, _response, variables) => {
    const { id } = variables as DeleteProjectRequest & { apiKey: string };
    queryClient.setQueryData<UserProject[]>(['projects'], prev =>
      prev ? prev.filter(project => project.id !== id) : []
    );
    queryClient.removeQueries({ queryKey: ['project', id] });
  },
});

const getEducation = queryEndpoint({
  entity: 'education',
  queryKey: ({ id }: GetEducationRequest & { apiKey: string }) => [
    'education',
    id,
  ],
  queryFn: ({ id, apiKey }, nextContext?) =>
    httpClient.auto<GetEducationResponse>({
      url: `/api/educations/${id}`,
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: null,
  nullable: true,
});

const getEducations = queryEndpoint({
  entity: 'educations',
  queryKey: () => ['educations'],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient.auto<GetEducationsResponse>({
      url: '/api/educations',
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: [],
});

const addEducation = mutationEndpoint({
  actionName: 'addEducation',
  mutationKey: ['add-education'],
  mutationFn: ({
    apiKey,
    ...payload
  }: AddEducationRequest & { apiKey: string }) =>
    httpClient.auto<GetEducationResponse>({
      url: '/api/educations',
      method: 'POST',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, response) => {
    queryClient.setQueryData<Education[]>(['educations'], prev => [
      ...(prev || []),
      response,
    ]);
    queryClient.setQueryData<Education>(
      ['education', response.id],
      () => response
    );
  },
});

const updateEducation = mutationEndpoint({
  actionName: 'updateEducation',
  mutationKey: ['update-education'],
  mutationFn: ({
    apiKey,
    id,
    ...payload
  }: UpdateEducationRequest & { apiKey: string }) =>
    httpClient.auto<GetEducationResponse>({
      url: `/api/educations/${id}`,
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, response) => {
    queryClient.setQueryData<Education[]>(['educations'], prev =>
      prev
        ? [
            ...prev.map(project =>
              project.id === response.id ? response : project
            ),
          ]
        : [response]
    );
    queryClient.setQueryData<Education>(
      ['education', response.id],
      () => response
    );
  },
});

const deleteEducation = mutationEndpoint({
  actionName: 'deleteEducation',
  mutationKey: ['delete-education'],
  mutationFn: ({ apiKey, id }: DeleteEducationRequest & { apiKey: string }) =>
    httpClient.auto<void>({
      url: `/api/educations/${id}`,
      method: 'DELETE',
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, _response, variables) => {
    const { id } = variables as DeleteEducationRequest & { apiKey: string };
    queryClient.setQueryData<Education[]>(['educations'], prev =>
      prev ? prev.filter(education => education.id !== id) : []
    );
    queryClient.removeQueries({ queryKey: ['education', id] });
  },
});

export {
  addEducation,
  addProject,
  deleteEducation,
  deleteProject,
  getEducation,
  getEducations,
  getProject,
  getProjects,
  updateEducation,
  updateProject,
};
